import { useSWRConfig } from "swr";
import { unstable_serialize } from "swr/infinite";

import { useCallback } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import classNames from "classnames";
import { twMerge } from "tailwind-merge";

import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faRss } from "@fortawesome/free-solid-svg-icons/faRss";

import { collectionUrl } from "components/CollectionPageLayout";
import { collectionsUrl } from "components/CollectionsPageLayout/CollectionsPageAll";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import useActivityTracker from "hooks/useActivityTracker";
import useAuthentication from "hooks/useAuthentication";
import { useGlobalState } from "hooks/useGlobalState";
import { fetchApi } from "utils/baseFetcher";

type FollowCollectionButtonProps = {
  collection: any;
  onFollow?: (_collection: any) => void;
  style?: "full" | "small" | "card" | "icon";
  hideTextOnActive?: boolean;
  showIcon?: boolean;
  className?: string;
};

const FollowCollectionButton = ({
  collection,
  onFollow,
  hideTextOnActive,
  showIcon,
  style,
  className,
}: FollowCollectionButtonProps) => {
  const { authenticated, account } = useAuthentication();
  const { trackEvent } = useActivityTracker();
  const { toggleModal } = useSignInSignUp();

  const { mutate } = useSWRConfig();

  const [isFollowed, setIsFollowed] = useGlobalState(
    `collection-follow-${collection?.id}`,
    collection?.isFollowed
  );

  const follow = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();

      if (!authenticated) {
        toggleModal("sign-in");
        return;
      }

      const action = isFollowed ? "unfollow" : "follow";
      setIsFollowed(!isFollowed);

      mutate(
        unstable_serialize(() => collectionsUrl(1, { isFollowed: true })),
        () => {},
        {
          populateCache: (_, pages) => {
            if (!pages) return [];

            if (action == "follow") {
              const firstPage = pages[0];
              return [
                {
                  ...firstPage,
                  data: [
                    { ...collection, isFollowed: true },
                    ...(firstPage?.data || []),
                  ],
                },
                ...pages.slice(1, pages.length),
              ];
            } else {
              return pages.map((page: any) => ({
                ...page,
                data: page.data.filter((c: any) => c.id !== collection.id),
              }));
            }
          },
          revalidate: false,
        }
      );

      [
        "generic",
        "recommended",
        "top-countries",
        "top-sectors",
        "top-content-verticals",
      ].forEach((segment) => {
        mutate(
          unstable_serialize(() => collectionsUrl(1, { segment })),
          () => {},
          {
            populateCache: (_, pages) => {
              return pages?.map((page: any) => ({
                ...page,
                data: page.data.map((c: any) => {
                  if (c.id === collection.id)
                    return {
                      ...c,
                      isFollowed: action === "follow",
                    };
                  return c;
                }),
              }));
            },
            revalidate: false,
          }
        );
      });

      mutate(collectionUrl(collection.slug), {
        ...collection,
        totalFollowers: collection.totalFollowers + (isFollowed ? -1 : 1),
      });

      fetchApi(`/api/v2/collections/${collection.slug}/${action}`, {
        method: "PATCH",
      })
        .then((response) => {
          trackEvent("follow", { following: !isFollowed });
          if (onFollow)
            onFollow({ ...collection, isFollowed: response.isFollowed });
        })
        .catch(() => {
          // setIsFollowed(!isFollowed);
        });
    },
    [
      authenticated,
      isFollowed,
      setIsFollowed,
      mutate,
      collection,
      toggleModal,
      trackEvent,
      onFollow,
    ]
  );

  if (!authenticated || account?.canFollow) {
    if (style === "icon") {
      return (
        <>
          <div>
            <button
              className={twMerge(
                "min-w-[68px]",
                "bg-transparent outline-none border-none",
                "flex flex-col items-center",
                "text-[13px] text-center",
                isFollowed ? "text-jade" : "text-white"
              )}
              aria-label="follow"
              onClick={follow}
            >
              <FontAwesomeIcon
                icon={isFollowed ? faCheck : faRss}
                className="text-[24px]"
              />
              {isFollowed ? "Following" : "Follow"}
            </button>
          </div>
        </>
      );
    }

    if (style === "card") {
      return (
        <>
          <div>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{isFollowed ? "Following" : "Follow"}</Tooltip>}
            >
              <button
                className={twMerge(
                  isFollowed
                    ? "bg-smart-teal hover:bg-[#1d8988] text-white"
                    : "bg-white hover:bg-smart-teal text-smart-teal hover:text-white ",
                  "border border-solid border-smart-teal ",
                  "rounded-full text-[13px] leading-[1] px-[10px] py-[6px]",
                  "flex"
                )}
                aria-label="follow"
                onClick={follow}
              >
                <FontAwesomeIcon icon={faRss} className="mr-[0.5em]" />
                {isFollowed ? "Following" : "Follow"}
              </button>
            </OverlayTrigger>
          </div>
        </>
      );
    }

    if (style === "small") {
      return (
        <>
          <div>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{isFollowed ? "Following" : "Follow"}</Tooltip>}
            >
              <button
                className={twMerge(
                  isFollowed
                    ? "bg-white border-bullish-green hover:bg-bullish-green text-bullish-green hover:text-white"
                    : "bg-white border-smart-teal  hover:bg-smart-teal text-smart-teal hover:text-white ",
                  "border border-solid",
                  "rounded-sm text-[12px] px-[1em] py-[0.75em]"
                )}
                aria-label="follow"
                onClick={follow}
              >
                <FontAwesomeIcon icon={isFollowed ? faCheck : faRss} />
              </button>
            </OverlayTrigger>
          </div>
        </>
      );
    }

    return (
      <>
        <div>
          <button
            className={classNames(
              isFollowed
                ? "bg-jade hover:bg-[#419855]"
                : "bg-smart-teal hover:bg-[#1d8988]",
              "border-none rounded-full",
              "text-white font-medium",
              "leading-[1] md:leading-normal",
              "text-[13px] md:text-[14px]",
              "px-[1.25em] py-[0.75em] md:px-[20px] md:py-[7px]",
              className
            )}
            aria-label="follow"
            onClick={follow}
          >
            {showIcon && (
              <FontAwesomeIcon
                icon={isFollowed ? faCheck : faPlus}
                className={twMerge(
                  (!hideTextOnActive || !isFollowed) && "mr-[5px]"
                )}
              />
            )}
            {(!hideTextOnActive || !isFollowed) &&
              (isFollowed ? "Following" : "Follow")}
          </button>
        </div>
      </>
    );
  }

  return <></>;
};

export default FollowCollectionButton;
